/**
 * Surface exit prompts after the last mission on a world map (normal + alternative routes).
 */
import {
  getSurfaceForMissionIndex,
  resolveActiveRouteForSurface,
  isAlternativePathEligibleSurface,
} from './exploreUnknownAlternativePath';

/** Must match server `surfaceChain` and `EXPLORE_SURFACE_DARKNESS_TIER` key order. */
export const EXPLORE_SURFACE_CHAIN = Object.freeze([
  'arrival',
  'cave',
  'desert',
  'fair',
  'lavender',
  'treetop',
  'boat',
  'pixelpast',
  'kendrex',
  'autobahn',
  'productionhell',
  'awakening',
  'core',
]);

/** Locale keys for continuing to the immediate next world (alternative route / pre-L20 normal). */
export const SURFACE_EXIT_TRANSITION_COPY = Object.freeze({
  arrival: {
    regionKey: 'caveEntranceRegion',
    bodyKey: 'caveEntranceBody',
    continueKey: 'enterCave',
  },
  cave: {
    regionKey: 'caveExitRegion',
    bodyKey: 'caveExitBody',
    continueKey: 'caveExitContinue',
  },
  desert: {
    regionKey: 'desertFairRegion',
    bodyKey: 'desertFairBody',
    continueKey: 'desertFairContinue',
  },
  fair: {
    regionKey: 'fairLavenderRegion',
    bodyKey: 'fairLavenderBody',
    continueKey: 'fairLavenderContinue',
  },
  lavender: {
    regionKey: 'lavenderTreetopRegion',
    bodyKey: 'lavenderTreetopBody',
    continueKey: 'lavenderTreetopContinue',
  },
  treetop: {
    regionKey: 'treetopBoatRegion',
    bodyKey: 'treetopBoatBody',
    continueKey: 'treetopBoatContinue',
  },
  boat: {
    regionKey: 'boatPixelPastRegion',
    bodyKey: 'boatPixelPastBody',
    continueKey: 'boatPixelPastContinue',
  },
  pixelpast: {
    regionKey: 'pixelPastKendrexRegion',
    bodyKey: 'pixelPastKendrexBody',
    continueKey: 'pixelPastKendrexContinue',
  },
  kendrex: {
    regionKey: 'kendrexAutobahnRegion',
    bodyKey: 'kendrexAutobahnBody',
    continueKey: 'kendrexAutobahnContinue',
  },
  autobahn: {
    regionKey: 'autobahnProductionHellRegion',
    bodyKey: 'autobahnProductionHellBody',
    continueKey: 'autobahnProductionHellContinue',
  },
  productionhell: {
    regionKey: 'productionHellAwakeningRegion',
    bodyKey: 'productionHellAwakeningBody',
    continueKey: 'productionHellAwakeningContinue',
  },
  awakening: {
    regionKey: 'awakeningCoreRegion',
    bodyKey: 'awakeningCoreBody',
    continueKey: 'awakeningCoreContinue',
  },
});

/**
 * @param {string} surface
 * @param {number[]} completedIndices
 * @param {Record<string, object>} plan
 * @param {Record<string, string>} routeChoices
 * @returns {boolean}
 */
export function isSurfaceCompleteForActiveRoute(surface, completedIndices, plan, routeChoices) {
  const resolved = resolveActiveRouteForSurface(surface, plan, routeChoices);
  if (!Array.isArray(resolved.missionSequence) || resolved.missionSequence.length === 0) {
    return false;
  }
  const completed = new Set(completedIndices || []);
  return resolved.missionSequence.every((missionIndex) => completed.has(missionIndex));
}

/**
 * @param {string} targetSurface
 * @param {number[]} completedIndices
 * @param {Record<string, object>} plan
 * @param {Record<string, string>} routeChoices
 * @returns {boolean}
 */
export function arePrerequisiteSurfacesCompleteForTarget(
  targetSurface,
  completedIndices,
  plan,
  routeChoices
) {
  const targetIdx = EXPLORE_SURFACE_CHAIN.indexOf(targetSurface);
  if (targetIdx <= 0) {
    return true;
  }
  for (let i = 0; i < targetIdx; i += 1) {
    const surface = EXPLORE_SURFACE_CHAIN[i];
    if (!isSurfaceCompleteForActiveRoute(surface, completedIndices, plan, routeChoices)) {
      return false;
    }
  }
  return true;
}

/**
 * @param {string | null | undefined} surface
 * @returns {number}
 */
export function getExploreSurfaceChainIndex(surface) {
  if (!surface) {
    return -1;
  }
  return EXPLORE_SURFACE_CHAIN.indexOf(surface);
}

/**
 * Whether L20+ alternative-path rules apply for surface-exit copy and normal-route world skip.
 * Route choice on a surface is only offered at L20+, but `exploreUnknownLevel` defaults to 1 in
 * React state until `/explore-unknown/progress` rehydrates after returning from battle.
 *
 * @param {number} exploreUnknownLevel
 * @param {number} alternativePathMinLevel
 * @param {Record<string, string>} routeChoices
 * @param {string} exploreSurface
 * @returns {boolean}
 */
export function isAlternativePathUnlockedForSurfaceExit(
  exploreUnknownLevel,
  alternativePathMinLevel,
  routeChoices,
  exploreSurface
) {
  if (Number(exploreUnknownLevel) >= alternativePathMinLevel) {
    return true;
  }
  const committedRoute = routeChoices?.[exploreSurface];
  return committedRoute === 'normal' || committedRoute === 'alternative';
}

/**
 * @param {{
 *   exploreSurface: string,
 *   completedIndices: number[],
 *   alternativePathPlan: Record<string, object>,
 *   routeChoices: Record<string, string>,
 *   exploreUnknownLevel: number,
 *   alternativePathMinLevel?: number,
 * }} params
 * @returns {{
 *   kind: 'continue' | 'normalSkip',
 *   fromSurface: string,
 *   targetSurface: string,
 *   skippedSurface?: string,
 *   promptSurface: string,
 *   regionKey: string,
 *   bodyKey: string,
 *   continueKey: string,
 * } | null}
 */
/**
 * @param {string} surface
 * @param {'normal'|'alternative'} route
 * @param {number[]} missionIndices
 * @param {(surface: string) => string} mapNameForSurface
 * @param {(surface: string) => string} previewImageUrlForSurface
 * @returns {{ missionIndex: number, destinationSurface: string, mapName: string, imageUrl: string } | null}
 */
export function tryBuildSurfaceRouteExitPreview(
  surface,
  route,
  missionIndices,
  mapNameForSurface,
  previewImageUrlForSurface,
  previewOptions = {}
) {
  const chainIdx = EXPLORE_SURFACE_CHAIN.indexOf(surface);
  if (chainIdx < 0 || chainIdx + 1 >= EXPLORE_SURFACE_CHAIN.length) {
    return null;
  }
  if (!Array.isArray(missionIndices) || missionIndices.length === 0) {
    return null;
  }
  let preview;
  try {
    preview = resolveRouteDestinationPreview(surface, route, previewOptions);
  } catch {
    return null;
  }
  const destinationSurface = preview.destinationSurface;
  return {
    missionIndex: missionIndices[missionIndices.length - 1],
    destinationSurface,
    mapName: mapNameForSurface(destinationSurface),
    imageUrl: previewImageUrlForSurface(destinationSurface),
  };
}

/**
 * Preview where a route lands after the player finishes the current world.
 * Normal-route world skip is only shown at L20+ (same gate as exit prompts).
 *
 * @param {string} surface
 * @param {'normal'|'alternative'} route
 * @param {{
 *   exploreUnknownLevel?: number,
 *   alternativePathMinLevel?: number,
 *   routeChoices?: Record<string, string>,
 * }} [options]
 * @returns {{ kind: 'skip'|'continue', destinationSurface: string, skippedSurface?: string }}
 */
export function resolveRouteDestinationPreview(surface, route, options = {}) {
  if (route !== 'normal' && route !== 'alternative') {
    throw new Error(
      `resolveRouteDestinationPreview: route must be "normal" or "alternative" (got ${JSON.stringify(route)})`
    );
  }
  const {
    exploreUnknownLevel = 0,
    alternativePathMinLevel = 20,
    routeChoices = {},
  } = options;
  const chainIdx = EXPLORE_SURFACE_CHAIN.indexOf(surface);
  if (chainIdx < 0) {
    throw new Error(`resolveRouteDestinationPreview: unknown surface (${String(surface)})`);
  }
  if (chainIdx + 1 >= EXPLORE_SURFACE_CHAIN.length) {
    throw new Error(`resolveRouteDestinationPreview: no next surface after ${String(surface)}`);
  }
  const immediateNext = EXPLORE_SURFACE_CHAIN[chainIdx + 1];

  if (route === 'alternative') {
    return { kind: 'continue', destinationSurface: immediateNext };
  }

  const normalSkipUnlocked = isAlternativePathUnlockedForSurfaceExit(
    exploreUnknownLevel,
    alternativePathMinLevel,
    routeChoices,
    surface
  );

  if (
    normalSkipUnlocked &&
    isAlternativePathEligibleSurface(surface) &&
    isAlternativePathEligibleSurface(immediateNext) &&
    chainIdx + 2 < EXPLORE_SURFACE_CHAIN.length
  ) {
    return {
      kind: 'skip',
      skippedSurface: immediateNext,
      destinationSurface: EXPLORE_SURFACE_CHAIN[chainIdx + 2],
    };
  }

  return { kind: 'continue', destinationSurface: immediateNext };
}

export function resolvePendingSurfaceExitTransition({
  exploreSurface,
  completedIndices,
  alternativePathPlan,
  routeChoices,
  exploreUnknownLevel,
  alternativePathMinLevel = 20,
}) {
  if (!exploreSurface || !Array.isArray(completedIndices)) {
    return null;
  }
  if (
    !isSurfaceCompleteForActiveRoute(
      exploreSurface,
      completedIndices,
      alternativePathPlan,
      routeChoices
    )
  ) {
    return null;
  }

  const chainIdx = EXPLORE_SURFACE_CHAIN.indexOf(exploreSurface);
  if (chainIdx < 0 || chainIdx + 1 >= EXPLORE_SURFACE_CHAIN.length) {
    return null;
  }

  const immediateNext = EXPLORE_SURFACE_CHAIN[chainIdx + 1];
  const route = routeChoices?.[exploreSurface] || 'normal';
  const altPathUnlocked = isAlternativePathUnlockedForSurfaceExit(
    exploreUnknownLevel,
    alternativePathMinLevel,
    routeChoices,
    exploreSurface
  );

  if (
    altPathUnlocked &&
    route === 'normal' &&
    isAlternativePathEligibleSurface(exploreSurface) &&
    isAlternativePathEligibleSurface(immediateNext) &&
    chainIdx + 2 < EXPLORE_SURFACE_CHAIN.length
  ) {
    const skippedSurface = immediateNext;
    const copy = SURFACE_EXIT_TRANSITION_COPY[skippedSurface];
    if (!copy) {
      return null;
    }
    return {
      kind: 'normalSkip',
      fromSurface: exploreSurface,
      skippedSurface,
      targetSurface: EXPLORE_SURFACE_CHAIN[chainIdx + 2],
      promptSurface: skippedSurface,
      regionKey: copy.regionKey,
      bodyKey: copy.bodyKey,
      continueKey: copy.continueKey,
    };
  }

  const copy = SURFACE_EXIT_TRANSITION_COPY[exploreSurface];
  if (!copy) {
    return null;
  }

  return {
    kind: 'continue',
    fromSurface: exploreSurface,
    targetSurface: immediateNext,
    promptSurface: exploreSurface,
    regionKey: copy.regionKey,
    bodyKey: copy.bodyKey,
    continueKey: copy.continueKey,
  };
}

/**
 * Block mission-progress sync from auto-advancing before the player confirms the exit prompt.
 * @param {string} from
 * @param {string} to
 * @param {{
 *   currentSurface: string,
 *   completedIndices: number[],
 *   alternativePathPlan: Record<string, object>,
 *   routeChoices: Record<string, string>,
 *   exploreUnknownLevel: number,
 * }} ctx
 * @returns {boolean}
 */
export function shouldDeferAutoSurfaceAdvance(from, to, ctx) {
  if (ctx.currentSurface !== from) {
    return false;
  }
  const pending = resolvePendingSurfaceExitTransition({
    exploreSurface: from,
    completedIndices: ctx.completedIndices,
    alternativePathPlan: ctx.alternativePathPlan,
    routeChoices: ctx.routeChoices,
    exploreUnknownLevel: ctx.exploreUnknownLevel,
  });
  if (!pending) {
    return false;
  }
  const completed = ctx.completedIndices || [];
  const progressedOntoSurface = (surface) =>
    completed.some((idx) => getSurfaceForMissionIndex(idx) === surface);
  if (progressedOntoSurface(to) || progressedOntoSurface(pending.targetSurface)) {
    return false;
  }
  const chainIdx = EXPLORE_SURFACE_CHAIN.indexOf(from);
  if (chainIdx < 0) {
    return false;
  }
  const immediateNext = EXPLORE_SURFACE_CHAIN[chainIdx + 1];
  return to === pending.targetSurface || to === immediateNext;
}
